import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Send, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

export default function RegenShare() {
  const { regen_token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | ready | sending | done | error
  const [request, setRequest] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/regen-share/${regen_token}`);
        setRequest(data);
        if (data.fulfilled_at) {
          setPhase("done");
        } else {
          setPhase("ready");
        }
      } catch (e) {
        const code = e?.response?.status;
        if (code === 410) setError("This renewal request has expired (72-hour window).");
        else if (code === 404) setError("This renewal link is invalid.");
        else setError(e?.response?.data?.detail || "Could not load this renewal request.");
        setPhase("error");
      }
    })();
  }, [regen_token]);

  const confirm = async () => {
    setPhase("sending");
    try {
      const { data } = await api.post(`/regen-share/${regen_token}/confirm`);
      setResult(data);
      setPhase("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate a fresh link.");
      setPhase("error");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header minimal />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">
          Renewal request
        </p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          {phase === "done" ? "Fresh link delivered" : "Approve fresh access?"}
        </h1>

        {phase === "loading" && (
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading request…
          </p>
        )}

        {phase === "error" && (
          <div data-testid="regen-error" className="mt-6 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
            <p className="text-sm text-slate-800 dark:text-slate-100">{error}</p>
          </div>
        )}

        {(phase === "ready" || phase === "sending") && request && (
          <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Requested by</p>
            <p className="mt-2 font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
              {request.recipient_name}
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">{request.recipient_email}</p>

            {request.message && (
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Their message</p>
                <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {request.message}
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={confirm}
                disabled={phase === "sending"}
                data-testid="regen-confirm-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
              >
                {phase === "sending"
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending fresh link…</>
                  : <>Approve &amp; send fresh link <Send className="h-4 w-4" /></>}
              </button>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                One click — a new 30-day link will be emailed straight to {request.recipient_email}.
              </p>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div data-testid="regen-done" className="mt-8 rounded-xl border border-teal-300 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-teal-700 dark:text-teal-300 mt-0.5" />
              <div>
                <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
                  {result ? "Fresh access link sent" : "This request was already fulfilled"}
                </p>
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                  {result
                    ? <>A new 30-day link has been emailed to <b>{result.recipient_email}</b>.</>
                    : <>A fresh link was issued earlier — no further action needed.</>}
                </p>
                {result?.share_url && (
                  <p className="mt-3 text-xs text-slate-600 dark:text-slate-300 break-all">
                    Direct link: <a className="underline" href={result.share_url}>{result.share_url}</a>
                  </p>
                )}
              </div>
            </div>
            <Link
              to="/account"
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Back to your account <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
